/**
 * Priest kit identity (plan 29 F9, lane CA 2026-10-11).
 *
 * Authored looks for the reachable priest abilities that resolved to a
 * generic `${id}_aura` pulse, a school default or a look borrowed from
 * another kit: Flash Mend's `quick_heal`, Greater Mend's `divine_heal`,
 * Renew's `renew_glow` and Word of Shielding's `holy_barrier` were never
 * registered; Prayer of Mending wore the paladin's holy_radiance_burst and
 * the gold `holy_heal` disc; Shadow Dart the rogue's shadow_burst; Shadow
 * Word: Pain an unregistered `shadow_curse`; the auto the shared
 * holy_sparkle. Each def is named by the ability's TOML (`cast_effect`,
 * `impact_effect`, `wind_up_effect`), so the caster's screen, the confirmed
 * hit or heal and a remote replay all read the same id.
 *
 * Heals: the cast def is the tell at the PRIEST; the impact def lands on
 * whoever the server healed (HealLandingVFX, from `playerHealed`) — never on
 * an enemy. Colours follow each TOML `aura_color`: Sacred Touch gold, Flash
 * Mend white, Greater Mend amber, Renew green, Prayer of Mending lavender,
 * Word of Shielding azure, Shadow Dart violet, Shadow Word: Pain deep
 * purple. Bursts end (one-shot tweens); states loop on alpha/scale/rotation
 * only; no screen shake or flash (plan 29 F8 — the hit tier owns it).
 */
import type { VFXEffectConfig } from '../../VFXEffectRegistry';
import { ringBlast, shardBurst, glintScatter, wispRise, starFlash, risingColumn } from './burstBuilders';

/** A pointed leaf/petal along angle `deg` (degrees) from `inner` to `outer` px, `half` px wide. */
function leaf(deg: number, inner: number, outer: number, half: number): number[] {
  const a = (deg * Math.PI) / 180;
  const c = Math.cos(a);
  const sn = Math.sin(a);
  const mid = (inner + outer) / 2;
  const at = (along: number, side: number) => [Math.round((c * along - sn * side) * 10) / 10, Math.round((sn * along + c * side) * 10) / 10];
  return [...at(inner, 0), ...at(mid, half), ...at(outer, 0), ...at(mid, -half)];
}

const GOLD = 0xffc21a;
const GOLD_HOT = 0xfff0a0;
const WHITE = 0xffffff;
const PEARL = 0xfff6d8;
/** A warm rim so Flash Mend's white reads on the pale player sprite. */
const MEND_RIM = 0xf0b030;
/**
 * Live VQA (lane CA): the player sprite draws at depth 100, so a tell on the
 * body must sit above it (or outside the 72 px sprite) to be seen at all.
 * Scattered copies start at 0.3 × their ring, inside the sprite, and only
 * move outward — tells that close IN are placed one by one instead. A spawn's
 * `duration` (the wind-up length, a heal's 600 ms) replaces every tween's.
 */
const OVER = 102;
/** A stroked ring closing onto the caster — reads from the first frame (the BY reticle grammar). */
function closingRing(color: number, hot: number, radius: number, durationMs: number, scaleTo = 0.5) {
  return [
    { type: 'arc' as const, arcRadius: radius, arcStartAngle: 0, arcEndAngle: 360, width: 3, color: hot, alpha: 0.9, depth: 101,
      tween: { scale: scaleTo, duration: durationMs, ease: 'Sine.easeIn' } },
    { type: 'arc' as const, arcRadius: radius + 6, arcStartAngle: 0, arcEndAngle: 360, width: 7, color, alpha: 0.25, depth: 100,
      tween: { scale: scaleTo, alpha: 0.6, duration: durationMs, ease: 'Sine.easeIn' } },
  ];
}
/** `count` small shapes on an ellipse (rx, ry) that each travel to (tx, ty) × their angle. */
function ringIn(count: number, rx: number, ry: number, inner: number, base: { radius: number; color: number; durationMs: number; delayMs?: number }) {
  return Array.from({ length: count }, (_, i) => {
    const a = (i / count) * Math.PI * 2;
    return {
      type: 'circle' as const, radius: base.radius, color: base.color, alpha: 0.95, depth: OVER, glow: true,
      offsetX: Math.round(Math.cos(a) * rx), offsetY: Math.round(Math.sin(a) * ry),
      tween: { x: Math.round(Math.cos(a) * rx * inner), y: Math.round(Math.sin(a) * ry * inner), alpha: 0.35, duration: base.durationMs, ease: 'Sine.easeIn', ...(base.delayMs ? { delay: base.delayMs } : {}) },
    };
  });
}
const AMBER = 0xff9e40;
const AMBER_HOT = 0xffd9a0;
const GREEN = 0x90ee90;
const LEAF = 0x4fc46a;
const LAVENDER = 0xc5a3ff;
const LAVENDER_HOT = 0xeee0ff;
const AZURE = 0x7fd4ff;
const AZURE_HOT = 0xe0f6ff;
const VIOLET = 0x9370db;
const VIOLET_DARK = 0x3d1a5c;
const PURPLE = 0x6a0dad;
const PURPLE_HOT = 0xc78bff;

/** Sacred Touch's muzzle: a gold mote leaves the hand. */
const SACRED_TOUCH_MOTE: VFXEffectConfig = {
  id: 'sacred_touch_mote',
  name: 'Sacred Touch Mote',
  category: 'burst',
  description: 'A small gold glow at the hand with a pale core as the mote leaves',
  primitives: [
    ringBlast({ radius: 8, color: GOLD, alpha: 0.6, stroke: { color: GOLD_HOT, width: 2 }, expandTo: 1.8, durationMs: 200 }),
    glintScatter({ count: 3, spread: 10, color: GOLD_HOT, durationMs: 180 }),
  ],
};

/** Sacred Touch's hit: a gold cross flares on the target. */
const SACRED_TOUCH_FLARE: VFXEffectConfig = {
  id: 'sacred_touch_flare',
  name: 'Sacred Touch Flare',
  category: 'burst',
  description: 'A gold four-point cross flares on the target with a thin gold ring and pale glints',
  primitives: [
    starFlash({ points: 4, innerRadius: 3, outerRadius: 18, color: GOLD_HOT, glow: true, spinRad: Math.PI * 0.25, expandTo: 1.4, durationMs: 240 }),
    ringBlast({ radius: 10, color: GOLD, alpha: 0.45, stroke: { color: GOLD_HOT, width: 2 }, expandTo: 2, durationMs: 280 }),
    glintScatter({ count: 4, spread: 16, color: PEARL, durationMs: 220, delayMs: 50 }),
  ],
};

/** Flash Mend's tell: a quick white spark between the priest's hands. */
const FLASH_MEND_SPARK: VFXEffectConfig = {
  id: 'flash_mend_spark',
  name: 'Flash Mend Spark',
  category: 'burst',
  description: 'A quick white star sparks at the priest\'s hands and a thin pearl ring snaps out',
  primitives: [
    // Live VQA (lane CA): white alone vanished on the pale player sprite — a
    // warm gold rim keeps the white reading. Offset up to the raised hands.
    { ...starFlash({ points: 6, innerRadius: 3, outerRadius: 15, color: WHITE, glow: true, expandTo: 1.6, durationMs: 240, depth: OVER }), offsetY: -30, stroke: { color: MEND_RIM, width: 2 } },
    { ...ringBlast({ radius: 12, color: PEARL, alpha: 0.3, stroke: { color: MEND_RIM, width: 2 }, expandTo: 2, durationMs: 280, depth: OVER }), offsetY: -30 },
  ],
};

/** Flash Mend's landing: a white bloom with a rising cross on the healed player. */
const FLASH_MEND_BLOOM: VFXEffectConfig = {
  id: 'flash_mend_bloom',
  name: 'Flash Mend Bloom',
  category: 'burst',
  description: 'Six white petals open around the healed player, a small cross rises out of them and pearl sparks lift',
  primitives: [
    ...[0, 60, 120, 180, 240, 300].map((deg) => ({
      type: 'polygon' as const, polygonPoints: leaf(deg, 22, 48, 7),
      color: WHITE, alpha: 0.9, depth: OVER, stroke: { color: MEND_RIM, width: 1.5 },
      tween: { alpha: 0, scale: 1.5, duration: 360, ease: 'Power2' },
    })),
    { type: 'rect', width: 5, height: 22, offsetY: -36, color: WHITE, alpha: 0.95, depth: OVER, glow: true,
      stroke: { color: MEND_RIM, width: 1.5 }, tween: { y: -58, alpha: 0, duration: 520, ease: 'Sine.easeOut' } },
    { type: 'rect', width: 16, height: 5, offsetY: -40, color: WHITE, alpha: 0.95, depth: OVER, glow: true,
      stroke: { color: MEND_RIM, width: 1.5 }, tween: { y: -62, alpha: 0, duration: 520, ease: 'Sine.easeOut' } },
    wispRise({ count: 5, spread: 18, radius: 2.2, color: PEARL, travel: 30, durationMs: 520, glow: true }),
  ],
};

/** Greater Mend's tell: amber light gathers in the priest's hands over the 800 ms wind-up. */
const GREATER_MEND_PRAYER: VFXEffectConfig = {
  id: 'greater_mend_prayer',
  name: 'Greater Mend Prayer',
  category: 'burst',
  description: 'Two amber orbs swell in the priest\'s hands, an amber ring brightens at the feet and amber motes draw in',
  primitives: [
    { type: 'ellipse', width: 70, height: 16, offsetY: 30, color: AMBER, alpha: 0.2, depth: 93,
      stroke: { color: AMBER_HOT, width: 3 },
      tween: { alpha: 0.75, scale: 1.1, duration: 700, ease: 'Sine.easeIn' } },
    ...[-30, 30].map((dx) => ({
      type: 'circle' as const, radius: 5, offsetX: dx, offsetY: 0, color: AMBER_HOT, alpha: 0.6, depth: OVER, glow: true,
      stroke: { color: AMBER, width: 2 }, tween: { scale: 1.9, alpha: 1, duration: 700, ease: 'Sine.easeIn' },
    })),
    ...ringIn(6, 54, 40, 0.6, { radius: 2.8, color: AMBER_HOT, durationMs: 700 }),
    ...closingRing(AMBER, AMBER_HOT, 56, 760, 0.55),
  ],
};

/** Greater Mend's landing: an amber halo settles on the healed player inside a soft column. */
const GREATER_MEND_HALO: VFXEffectConfig = {
  id: 'greater_mend_halo',
  name: 'Greater Mend Halo',
  category: 'burst',
  description: 'An amber halo settles from above onto the healed player inside a wide soft column, with a warm ring at the feet',
  primitives: [
    risingColumn({ width: 40, height: 96, color: AMBER, alpha: 0.35, glow: true, riseBy: 14, durationMs: 560 }),
    { type: 'ellipse', width: 40, height: 11, offsetY: -58, color: AMBER, alpha: 0.35, depth: OVER,
      stroke: { color: AMBER_HOT, width: 3 },
      tween: { y: -32, alpha: 0, duration: 620, ease: 'Sine.easeIn' } },
    ringBlast({ radius: 20, color: AMBER, alpha: 0.4, stroke: { color: AMBER_HOT, width: 2 }, expandTo: 2, durationMs: 420, delayMs: 120 }),
    wispRise({ count: 6, spread: 22, radius: 2.5, color: AMBER_HOT, travel: 34, durationMs: 600, delayMs: 100, glow: true }),
  ],
};

/** Renew's tell: green leaves spiral up the priest. */
const RENEW_CAST: VFXEffectConfig = {
  id: 'renew_cast',
  name: 'Renew',
  category: 'burst',
  description: 'Small green leaves spin up around the priest and a pale green ring opens at the feet',
  primitives: [
    ...[0, 120, 240].map((deg, i) => ({
      type: 'polygon' as const, polygonPoints: leaf(deg + 30, -6, 6, 3.5),
      offsetX: Math.round(Math.cos((deg * Math.PI) / 180) * 34), offsetY: 18,
      color: i === 1 ? GREEN : LEAF, alpha: 0.95, depth: OVER,
      tween: { y: -44, rotation: Math.PI * 2, alpha: 0, duration: 620, ease: 'Sine.easeOut', delay: i * 70 },
    })),
    { type: 'ellipse', width: 50, height: 12, offsetY: 30, color: GREEN, alpha: 0.35, depth: 93,
      stroke: { color: GREEN, width: 2 },
      tween: { alpha: 0, scale: 1.6, duration: 480, ease: 'Sine.easeOut' } },
  ],
};

/** Renew's state: three leaves circle the healed player while the heal-over-time runs. */
const RENEW_STATE: VFXEffectConfig = {
  id: 'renew_state',
  name: 'Renew State',
  category: 'status',
  description: 'Three green leaves circle the player and a faint green ring breathes at the feet: healing while it lasts',
  followTarget: 'player',
  primitives: [
    { type: 'ellipse', width: 52, height: 12, offsetY: 30, color: GREEN, alpha: 0.18, depth: 93,
      stroke: { color: GREEN, width: 2 },
      tween: { alpha: 0.32, scale: 1.06, duration: 900, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    { type: 'ellipse', width: 6, height: 11, color: LEAF, alpha: 0.9, depth: 101, orbit: { radius: 34, speed: 0.9 } },
    { type: 'ellipse', width: 6, height: 11, color: GREEN, alpha: 0.9, depth: 101, orbit: { radius: 34, speed: 0.9, startAngle: Math.PI * 2 / 3 } },
    { type: 'ellipse', width: 6, height: 11, color: LEAF, alpha: 0.9, depth: 101, orbit: { radius: 34, speed: 0.9, startAngle: Math.PI * 4 / 3 } },
  ],
};

/** Prayer of Mending's wind-up: lavender prayer beads close in on the priest. */
const PRAYER_OF_MENDING_VIGIL: VFXEffectConfig = {
  id: 'prayer_of_mending_vigil',
  name: 'Prayer of Mending Vigil',
  category: 'burst',
  description: 'A ring of eight lavender prayer beads closes in on the priest while a lavender ring brightens at the feet',
  primitives: [
    ...ringIn(8, 58, 42, 0.62, { radius: 4, color: LAVENDER_HOT, durationMs: 480 }),
    ...closingRing(LAVENDER, LAVENDER_HOT, 50, 480, 0.6),
    { type: 'ellipse', width: 72, height: 16, offsetY: 30, color: LAVENDER, alpha: 0.2, depth: 93,
      stroke: { color: LAVENDER_HOT, width: 3 }, tween: { alpha: 0.7, scale: 0.9, duration: 480, ease: 'Sine.easeIn' } },
  ],
};

/** Prayer of Mending's release: a lavender wave rolls out to the heal radius (fitted to it). */
const PRAYER_OF_MENDING_WAVE: VFXEffectConfig = {
  id: 'prayer_of_mending_wave',
  name: 'Prayer of Mending Wave',
  category: 'burst',
  description: 'A soft lavender wave and a bright lavender rim roll out from the priest to the edge of the heal',
  primitives: [
    ringBlast({ radius: 60, color: LAVENDER, alpha: 0.28, stroke: { color: LAVENDER_HOT, width: 3 }, expandTo: 2.5, durationMs: 520, ease: 'Sine.easeOut' }),
    ringBlast({ radius: 40, color: LAVENDER_HOT, alpha: 0.2, expandTo: 2.2, durationMs: 420, delayMs: 60 }),
    glintScatter({ count: 10, spread: 110, color: LAVENDER_HOT, radius: 3, durationMs: 420, delayMs: 140 }),
  ],
};

/** Prayer of Mending's landing: a lavender star touches each healed friend. */
const PRAYER_OF_MENDING_TOUCH: VFXEffectConfig = {
  id: 'prayer_of_mending_touch',
  name: 'Prayer of Mending Touch',
  category: 'burst',
  description: 'A soft lavender star and ring touch the healed player and beads drift up',
  primitives: [
    starFlash({ points: 5, innerRadius: 4, outerRadius: 17, color: LAVENDER_HOT, glow: true, expandTo: 1.6, durationMs: 340, depth: OVER }),
    ringBlast({ radius: 16, color: LAVENDER, alpha: 0.4, stroke: { color: LAVENDER_HOT, width: 2 }, expandTo: 1.8, durationMs: 360 }),
    wispRise({ count: 4, spread: 16, radius: 2.5, color: LAVENDER, travel: 28, durationMs: 520, glow: true }),
  ],
};

/** Word of Shielding's cast: an azure ward snaps around the shielded player. */
const WORD_OF_SHIELDING_CAST: VFXEffectConfig = {
  id: 'word_of_shielding_cast',
  name: 'Word of Shielding',
  category: 'burst',
  description: 'An azure hexagon closes in around the shielded player and seals with a bright flash of its rim',
  primitives: [
    { type: 'polygon', polygonPoints: [0, -54, 47, -27, 47, 27, 0, 54, -47, 27, -47, -27],
      color: AZURE, alpha: 0.05, depth: OVER, stroke: { color: AZURE_HOT, width: 3 },
      tween: { scale: 0.82, alpha: 0.7, duration: 220, ease: 'Sine.easeIn' } },
    glintScatter({ count: 6, spread: 46, color: AZURE_HOT, durationMs: 260, delayMs: 180 }),
  ],
};

/** Word of Shielding's state: an azure hexagonal ward while the absorb holds. */
const WORD_OF_SHIELDING_STATE: VFXEffectConfig = {
  id: 'word_of_shielding_state',
  name: 'Word of Shielding State',
  category: 'status',
  description: 'A pale azure hexagonal ward around the body with a bright rim and a turning glint: damage is soaked while it holds',
  followTarget: 'player',
  primitives: [
    { type: 'polygon', polygonPoints: [0, -50, 43, -25, 43, 25, 0, 50, -43, 25, -43, -25],
      color: AZURE, alpha: 0.16, depth: 99, stroke: { color: AZURE_HOT, width: 2.5 },
      tween: { alpha: 0.28, scale: 1.03, duration: 750, yoyo: true, repeat: -1, ease: 'Sine.easeInOut' } },
    { type: 'star', points: 4, innerRadius: 2, outerRadius: 7, color: AZURE_HOT, alpha: 0.95, depth: 101, glow: true,
      orbit: { radius: 47, speed: 0.7 } },
  ],
};

/** Shadow Dart's wind-up: violet motes pull into the hand over 300 ms. */
const SHADOW_DART_GATHER: VFXEffectConfig = {
  id: 'shadow_dart_gather',
  name: 'Shadow Dart Gather',
  category: 'burst',
  description: 'A dark violet core swells over the priest while violet motes flare around it',
  primitives: [
    { type: 'circle', radius: 3, color: VIOLET, alpha: 0.9, depth: 98, glow: true,
      scatter: { count: 6, radius: 26 }, tween: { scale: 0.3, alpha: 0.2, duration: 300, ease: 'Sine.easeIn' } },
    ...closingRing(VIOLET_DARK, VIOLET, 40, 300, 0.45),
    { type: 'circle', radius: 6, color: VIOLET_DARK, alpha: 0.4, depth: OVER,
      stroke: { color: VIOLET, width: 2 }, tween: { scale: 1.6, alpha: 0.9, duration: 300, ease: 'Sine.easeIn' } },
  ],
};

/** Shadow Dart's hit: the dart pierces and violet splinters fly. */
const SHADOW_DART_PIERCE: VFXEffectConfig = {
  id: 'shadow_dart_pierce',
  name: 'Shadow Dart Pierce',
  category: 'burst',
  description: 'A violet dart-head flash, a dark ring and violet splinters thrown out of the target',
  primitives: [
    { type: 'polygon', polygonPoints: [-16, -3, 10, 0, -16, 3], color: VIOLET, alpha: 0.95, depth: 98, glow: true,
      tween: { x: 12, alpha: 0, duration: 180, ease: 'Power3' } },
    ringBlast({ radius: 10, color: VIOLET_DARK, alpha: 0.55, stroke: { color: VIOLET, width: 2 }, expandTo: 2.2, durationMs: 280 }),
    shardBurst({ count: 6, spread: 18, color: VIOLET, shard: { width: 7, height: 2 }, durationMs: 280, glow: true }),
  ],
};

/** Shadow Word: Pain's muzzle: a dark whisper curls off the hand. */
const SHADOW_WORD_PAIN_WHISPER: VFXEffectConfig = {
  id: 'shadow_word_pain_whisper',
  name: 'Shadow Word: Pain Whisper',
  category: 'burst',
  description: 'A curl of deep-purple smoke and a faint ring as the word leaves the priest\'s hand',
  primitives: [
    { type: 'arc', arcRadius: 10, arcStartAngle: -160, arcEndAngle: 60, width: 3, color: PURPLE_HOT, alpha: 0.85, depth: 97,
      tween: { rotation: Math.PI, scale: 1.6, alpha: 0, duration: 300, ease: 'Sine.easeOut' } },
    wispRise({ count: 4, spread: 10, radius: 3, color: PURPLE, travel: 18, durationMs: 380 }),
  ],
};

/** Shadow Word: Pain's hit: a shadow sigil brands the target. */
const SHADOW_WORD_PAIN_MARK: VFXEffectConfig = {
  id: 'shadow_word_pain_mark',
  name: 'Shadow Word: Pain Mark',
  category: 'burst',
  description: 'A deep-purple three-spiked sigil brands the target inside a dark ring and shadow drips from it',
  primitives: [
    { type: 'circle', radius: 16, color: PURPLE, alpha: 0.3, depth: 96, stroke: { color: PURPLE_HOT, width: 2 },
      tween: { alpha: 0, scale: 1.4, duration: 520, ease: 'Sine.easeOut', delay: 120 } },
    starFlash({ points: 3, innerRadius: 4, outerRadius: 14, color: PURPLE_HOT, glow: true, spinRad: Math.PI / 3, expandTo: 1.25, durationMs: 460 }),
    wispRise({ count: 4, spread: 12, radius: 2.5, color: PURPLE, direction: 'down', travel: 20, durationMs: 520, delayMs: 120 }),
  ],
};

export const PRIEST_KIT_VFX: VFXEffectConfig[] = [
  SACRED_TOUCH_MOTE,
  SACRED_TOUCH_FLARE,
  FLASH_MEND_SPARK,
  FLASH_MEND_BLOOM,
  GREATER_MEND_PRAYER,
  GREATER_MEND_HALO,
  RENEW_CAST,
  RENEW_STATE,
  PRAYER_OF_MENDING_VIGIL,
  PRAYER_OF_MENDING_WAVE,
  PRAYER_OF_MENDING_TOUCH,
  WORD_OF_SHIELDING_CAST,
  WORD_OF_SHIELDING_STATE,
  SHADOW_DART_GATHER,
  SHADOW_DART_PIERCE,
  SHADOW_WORD_PAIN_WHISPER,
  SHADOW_WORD_PAIN_MARK,
];
